import { type ColumnDef, PageContent, RelatedListPage, type SortState } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDocumentTitle } from '../documentTitle';
import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import type { ObjectKey } from '../navigation';
import { NotFoundPage } from '../NotFoundPage';
import { withLookupLinks } from './links';
import { metadataFor } from './ObjectRecordView';
import {
  type ObjectListPage as Page,
  type ObjectRow,
  parseObjectListResponse,
  parseRecordResponse,
  recordUrl,
  relatedUrl,
} from './objectsApi';
import { queryParams, RELATED_MAX_LIMIT, serverSortable } from './relatedQuery';
import { isObjectKey, listPath, recordPath } from './routes';

const PAGE_SIZE = 20;

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

/** The object a related row opens: the list's object, else the row's own (lists of mixed records). */
function rowObject(listObject: string | undefined, row: ObjectRow): ObjectKey | null {
  const object = listObject ?? (typeof row.object === 'string' ? row.object : '');
  return isObjectKey(object) ? object : null;
}

/**
 * A related list's "View All" page at /<object>/:id/related/:relation, in the
 * library's RelatedListPage, with the record names and lookups linked as on
 * the record page. A list that fits in one request is loaded whole, and is
 * searched, filtered, sorted and paged in the browser; a longer one is paged,
 * sorted and searched by the server.
 */
export function ObjectRelatedListView({ object }: { object: ObjectKey }): JSX.Element {
  const { id = '', relation = '' } = useParams();
  const navigate = useNavigate();
  const base = objects[object];
  const [record, setRecord] = useState<ObjectRow | null>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | undefined>(undefined);
  const [search, setSearch] = useState('');
  // Whether the server pages this list, once the first request says how long it is.
  const [paged, setPaged] = useState<boolean | null>(null);
  const [result, setResult] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  useDocumentTitle(record ? base.titleOf?.(record) : base.plural);

  // The record, for its title and (audiences) which kind of members it holds.
  useEffect(() => {
    const controller = new AbortController();
    authenticatedFetch(recordUrl(object, id), { signal: controller.signal })
      .then(parseRecordResponse)
      .then(
        (next) => !controller.signal.aborted && setRecord(next),
        () => undefined,
      );
    return () => controller.abort();
  }, [object, id]);

  const list = (record ? metadataFor(object, record) : base).related?.find((r) => r.key === relation);

  useEffect(() => {
    if (!list) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // First every row, up to the server's limit; only a longer list is paged.
    const url = paged
      ? relatedUrl(object, id, relation, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, ...queryParams({ sort, search }) })
      : relatedUrl(object, id, relation, { limit: RELATED_MAX_LIMIT, offset: 0 });
    authenticatedFetch(url, { signal: controller.signal })
      .then(parseObjectListResponse)
      .then(
        (next) => {
          if (controller.signal.aborted) return;
          if (paged === null) setPaged(next.total > next.rows.length);
          setResult(next);
          setLoading(false);
        },
        (failure: unknown) => {
          if (controller.signal.aborted) return;
          setError(messageOf(failure, 'The related records could not be loaded.'));
          setLoading(false);
        },
      );
    return () => controller.abort();
  }, [object, id, relation, page, attempt, Boolean(list), paged, paged ? sort : undefined, paged ? search : undefined]);

  const columns = useMemo((): ColumnDef<ObjectRow>[] => {
    if (!list) return [];
    const linked = list.columns.map((c: ColumnDef<ObjectRow>) =>
      c.primary && !c.href
        ? { ...c, href: (row: ObjectRow) => { const target = rowObject(list.object, row); return target ? recordPath(target, row.id) : null; } }
        : c,
    );
    const columns = withLookupLinks(linked);
    return paged ? serverSortable(columns, result?.rows[0]) : columns;
  }, [list, paged, result]);

  if (!list) return <NotFoundPage />;

  const title = record ? (base.titleOf?.(record) ?? base.singular) : base.singular;
  return (
    <PageContent as="section" width="wide">
      <RelatedListPage<ObjectRow>
        title={list.label}
        objectLabel={base.singular}
        icon={base.icon}
        breadcrumbs={[
          { label: base.plural, href: listPath(object), onClick: () => navigate(listPath(object)) },
          { label: title, href: recordPath(object, id), onClick: () => navigate(recordPath(object, id)) },
          { label: list.label },
        ]}
        columns={columns}
        rows={result?.rows ?? []}
        getRowId={(row) => row.id}
        onRowClick={(row) => {
          const target = rowObject(list.object, row);
          if (target) navigate(recordPath(target, row.id));
        }}
        searchLabel={list.label}
        pageSize={PAGE_SIZE}
        {...(paged
          ? {
              mode: 'server' as const,
              page,
              total: result?.total ?? 0,
              onPageChange: setPage,
              ...(sort ? { sort } : {}),
              onSortChange: (next: SortState) => { setSort(next); setPage(1); },
              search,
              onSearchChange: (next: string) => { setSearch(next); setPage(1); },
            }
          : {})}
        loading={loading && (result === null || paged === true)}
        {...(error ? { error, onRetry: () => setAttempt((n) => n + 1) } : {})}
        emptyLabel={`No ${list.label.toLowerCase()} yet`}
      />
    </PageContent>
  );
}
